import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useDebounce } from 'use-debounce'
import { Check, ChevronsUpDown, LayoutGrid, Plus, Settings2 } from 'lucide-react'
import { useAuth } from '@/providers/auth'
import { usePermission } from '@/providers/permissions'
import { useMyAccess } from '@/hooks/useMyAccess'
import { useCompanyConfig } from '@/hooks/useCompanyConfig'
import { ADMIN_HOME, companiesOf, membershipOf, type AccessCompanyRef } from '@/lib/access'
import { listAdminCompanies, mergeCompanies } from '@/lib/companies'
import { companySettingsSectionOf } from '@/lib/companySettings'
import { initials } from '@/lib/format'
import { routeAfterCompanySwitch } from '@/lib/companySwitch'
import { imageThumbnail } from '@/lib/imageUpload'
import { cn } from '@/lib/utils'
import { AdminBadge } from '@/components/common/AdminBadge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { MenuSearch } from './MenuSearch'

const SEARCH_FROM = 6
const ADMIN_SEARCH_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300

const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const MARK_SIZE = {
  sm: 'h-6 w-6 rounded-md text-[9px]',
  md: 'h-8 w-8 rounded-lg text-[11px]'
}

function useSettingsLink(companyId?: string): string | null {
  const { hasPermission } = usePermission()
  return companyId && hasPermission('settings', 'read') ? `/empresas/${companyId}/configuracoes` : null
}

export function CompanyMark({ name, logoUrl, size = 'md' }: { name: string; logoUrl?: string | null; size?: keyof typeof MARK_SIZE }) {
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center overflow-hidden font-semibold shadow-xs',
        MARK_SIZE[size],
        logoUrl ? 'border border-border bg-card' : 'bg-primary text-primary-foreground'
      )}
    >
      {logoUrl ? <img src={imageThumbnail(logoUrl, 96)} alt="" className="h-full w-full object-contain p-0.5" /> : initials(name)}
    </span>
  )
}

function useAdminCompanies(enabled: boolean, query: string) {
  const [term] = useDebounce(query.trim(), SEARCH_DEBOUNCE_MS)
  return useQuery(['admin-companies', 'switcher', term], () => listAdminCompanies({ search: term, perPage: ADMIN_SEARCH_SIZE, sort: 'name:asc' }), {
    enabled,
    keepPreviousData: true,
    staleTime: 30_000
  })
}

export function CompanySwitcher({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const { company, setSelectedCompany } = useAuth()
  const { data: access } = useMyAccess()
  const { data: config } = useCompanyConfig()
  const settingsLink = useSettingsLink(company?.id)
  const { pathname } = useLocation()
  const settingsActive = !!companySettingsSectionOf(pathname)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const isSuperAdmin = !!access?.isSuperAdmin
  const adminCompanies = useAdminCompanies(isSuperAdmin && open, query)
  const companies = useMemo(() => companiesOf(access), [access])
  const name = company?.name || 'Selecionar imobiliária'
  const adminAccess = isSuperAdmin && !membershipOf(access, company?.id)

  const members = useMemo(() => {
    const term = normalize(query.trim())
    if (!term) return companies
    return companies.filter(item => normalize(item.name).includes(term))
  }, [companies, query])

  const visible = useMemo<AccessCompanyRef[]>(
    () => (isSuperAdmin ? mergeCompanies<AccessCompanyRef>(members, adminCompanies.data?.rows ?? []) : members),
    [isSuperAdmin, members, adminCompanies.data]
  )
  const searching = isSuperAdmin && adminCompanies.isLoading
  const status = searching
    ? 'Carregando imobiliárias...'
    : isSuperAdmin && adminCompanies.isError
      ? 'Não foi possível carregar as imobiliárias'
      : (isSuperAdmin || companies.length > 0) && visible.length === 0
        ? 'Nenhuma imobiliária encontrada'
        : null

  const go = (path: string) => {
    onNavigate?.()
    navigate(path)
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (!next) setQuery('')
      }}
    >
      <DropdownMenuTrigger
        aria-label={`Imobiliária ${name}. Configurações e troca de imobiliária`}
        data-testid="company-switcher"
        data-tour="agency"
        className={cn(
          'flex min-w-0 items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-sidebar-accent',
          collapsed ? 'h-10 w-10 justify-center' : 'w-full p-1.5 pr-2'
        )}
      >
        <CompanyMark name={name} logoUrl={config?.logo?.url} />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-sm font-semibold text-foreground">{name}</span>
              <span className="block truncate text-xs text-muted-foreground">{adminAccess ? 'Acesso de administrador' : 'Habitar'}</span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side={collapsed ? 'right' : 'bottom'} sideOffset={collapsed ? 10 : 6} className="w-72">
        {settingsLink && (
          <>
            <DropdownMenuItem asChild>
              <Link
                to={settingsLink}
                onClick={onNavigate}
                aria-current={settingsActive ? 'page' : undefined}
                className={cn('cursor-pointer gap-2.5', settingsActive && 'font-semibold text-primary')}
              >
                <Settings2 /> Configurações da imobiliária
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
          {isSuperAdmin ? 'Todas as imobiliárias' : 'Trocar de imobiliária'}
          {isSuperAdmin && <AdminBadge />}
        </DropdownMenuLabel>
        {(isSuperAdmin || companies.length >= SEARCH_FROM) && <MenuSearch value={query} onChange={setQuery} placeholder="Buscar imobiliária..." />}
        {status && visible.length === 0 && <DropdownMenuItem disabled>{status}</DropdownMenuItem>}
        <div className="max-h-64 overflow-y-auto">
          {visible.map(item => {
            const active = item.id === company?.id
            return (
              <DropdownMenuItem
                key={item.id}
                className="gap-2.5"
                onClick={() => {
                  if (active) return
                  setSelectedCompany({ id: item.id, name: item.name })
                  const next = routeAfterCompanySwitch(pathname, item.id)
                  if (next !== pathname) navigate(next)
                  onNavigate?.()
                }}
              >
                <CompanyMark name={item.name} size="sm" />
                <span className={cn('min-w-0 flex-1 truncate', active && 'font-semibold')}>{item.name}</span>
                {item.isActive === false && <span className="shrink-0 text-[11px] text-muted-foreground">Desativada</span>}
                {active && <Check className="h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            )
          })}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2" onClick={() => go(isSuperAdmin ? ADMIN_HOME : '/empresas')}>
          <LayoutGrid className="h-4 w-4" /> Ver todas as imobiliárias
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2 text-primary focus:text-primary" onClick={() => go('/primeiros-passos/criar-empresa')}>
          <Plus className="h-4 w-4" /> Nova imobiliária
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
